import React from 'react';
import { DynamicAssets, DynamicAssetsKeys, NavItem, ValuePropositionContent } from './models';
import { getPartnerResource } from '.';
import styled from 'styled-components';
import { CallToAction } from '../styling/styles';
import { Link } from 'react-router-dom';
import { Heading1, Subtitle, Heading4, BodyText } from '../components/Typography';
import { ProtectItCta } from './components';
import CalculatorHeader from './components/CalculatorHeader';
import { DisclaimerLink } from '../components/Disclaimer';
import BREAK_POINTS from '../styling/media-queries';
import ProtectItTitle from './components/ProtectItTitle';
import ProtectItContainer from './components/ProtectItContainer';
import ProtectItTextContainer from './components/ProtectItTextContainer';
import ValuePropTitle from './components/ValuePropTitle';
import CalculatorResultsHeader from './components/CalculatorResultsHeader';
import FinePrintTitle from './components/FinePrintTitle';
import TestimonialTitle from './components/TestimonialTitle';
import ValuePropositionImageContainer from './components/ValuePropositionImageContainer';
import EnrollmentTitle from './components/EnrollmentTitle';
import HeroContentSpacer from './components/HeroContentSpacer';
import SignupImageContainer from './components/SignupImageContainer';
import CompetitorPriceCell from './components/CalculatorCompetitorPriceCell';
import { RESIDENCE_PAGE_NUMBER, REVIEW_PAGE_NUMBER, LinkId } from '../constants';
import { getTotalPurchaseAmount } from '../pages/signup/utils';
import { phoneToUrl } from '../utils';
import WhatsCoveredCallToActionTitle from './components/WhatsCoveredCallToActionTitle';
import HeaderCallToAction from './components/HeaderCallToAction';
import { getPath } from '../pages/auth/utils';
import { AuthPath } from '../pages/auth/models';

const EXPERT_SESSIONS_PATH = '/expert-sessions';
const PRODUCT_NAME = 'Home+';
const CHAT_URL = '';
const HELP_NUMBER = '(844) 529-2692';

const headerLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.HeaderMenuFaq,
    label: 'FAQ',
    to: '/faq',
  },
  {
    id: LinkId.HeaderMenuClaims,
    label: 'File a claim',
    to: '{{ claimUrl }}',
    target: '_blank',
  },
  {
    id: LinkId.HeaderMenuHelpNumber,
    label: `Need help? ${HELP_NUMBER}`,
    to: phoneToUrl(HELP_NUMBER),
  },
];

const loggedInHeaderLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.HeaderMenuFaq,
    label: 'FAQs',
    to: '/faq',
  },
  {
    id: LinkId.HeaderMenuDashboard,
    label: 'Dashboard',
    to: '/dashboard',
  },
  {
    id: LinkId.HeaderMenuLogout,
    label: 'Log out',
    to: getPath(AuthPath.Logout),
  },
];

const footerLinks: NavItem[] = [
  {
    id: LinkId.FooterMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.FooterMenuClaims,
    label: 'File a claim',
    to: '{{ claimUrl }}',
    target: '_blank',
  },
  {
    id: LinkId.FooterMenuFaq,
    label: 'FAQ',
    to: '/faq',
  },
];

const moreInformationComponents = {
  InfoLinkContainer: styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 10px;
  `,
  InfoLink: styled(Link)<{ $light: boolean }>`
    display: flex;
    align-items: center;
    font-size: 18px;
    padding-bottom: 4px;
    padding-right: 5px;
    font-weight: 600;
    text-decoration-line: none;
    color: ${(props) => (props.$light ? props.theme.colors.linkColorLight : props.theme.colors.linkColorDark)};
  `,
  Arrow: styled.img`
    margin-left: 6px;
    margin-top: -3px;
  `,
};

const MoreInformationLink: React.FC<{ text: string; to: string; light: boolean }> = ({ text, to, light }) => (
  <moreInformationComponents.InfoLinkContainer>
    <moreInformationComponents.InfoLink $light={light} to={to}>
      {text}
      <moreInformationComponents.Arrow src={require('./resources/value-prop-link-arrow.svg')} />
    </moreInformationComponents.InfoLink>
  </moreInformationComponents.InfoLinkContainer>
);

export const valuePropositionContent: ValuePropositionContent[] = [
  {
    icon: getPartnerResource('get-refund-icon.svg'),
    caption: 'Get money back',
    title: (
      <>
        See if you can get some <br /> money back from old <br /> tech warranties
      </>
    ),
    subtitle: (
      <>
        With Asurion Home, you may
        <br /> not need your old tech warranties
        <br /> anymore.
      </>
    ),
    points: [],
    image: getPartnerResource('get-refund-image.jpg'),
    imageMargins: { desktop: { right: true, bottom: true, top: true }, mobile: {} },
    contentMargins: { desktop: { left: true, bottom: true, top: true }, mobile: {} },
    link: <MoreInformationLink light={true} text="Show me how" to="/dashboard/warranty-selection" />,
    shouldShowBanner: false,
    label: 'refund',
  },
  {
    icon: '',
    caption: '',
    title: <>Wherever (or whenever) you bought it</>,
    subtitle: '',
    points: ['Older TV? Covered', <>Brand new Laptop? Covered</>, <>Future purchases? Automatically covered.</>],
    image: getPartnerResource('expertise_techyoulove.svg'),
    imageMargins: { desktop: { left: true, bottom: true, top: true }, mobile: {} },
    contentMargins: { desktop: { right: true, bottom: true, top: true }, mobile: {} },
    link: <MoreInformationLink light={false} text="SEE WHAT'S COVERED" to="/whats-covered" />,
  },
  {
    icon: '',
    caption: '',
    title: (
      <>
        Accidents happen.
        <br />
        Now you&rsquo;re ready.
      </>
    ),
    subtitle: '',
    points: [
      <>Crack, spill or mechanical breakdown? We&rsquo;ll fix it!</>,
      <>Can&rsquo;t fix it? We&rsquo;ll replace it</>,
      <>Can&rsquo;t replace it? We&rsquo;ll reimburse you</>,
    ],
    image: getPartnerResource('BecauseThingsHappen.svg'),
    imageMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { left: true, top: true, bottom: true }, mobile: {} },
  },
  {
    icon: '',
    caption: '',
    title: (
      <>
        Receipts get lost.
        <br /> You&rsquo;re still covered.
      </>
    ),
    subtitle: '',
    points: [
      <>No registration forms to fill out</>,
      <>No receipts to hunt down</>,
      <>No expiration dates to keep track of</>,
    ],
    image: getPartnerResource('coffee_girl.svg'),
    imageMargins: { desktop: { left: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
  },
  {
    icon: '',
    caption: '',
    title: <>Fast answers to your tech questions</>,
    subtitle: '',
    points: [
      'Your Experts are just a call or click away',
      'Screen sharing, so we can see what you see',
      'We resolve millions of tech issues every month, with a 4.8 star rating',
    ],
    image: getPartnerResource('expert_male.svg'),
    imageMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { left: true, top: true, bottom: true }, mobile: { bottom: true } },
  },
  {
    icon: '',
    caption: '',
    title: <>Learn to do more with your tech</>,
    subtitle: '',
    points: [
      'Which streaming device is best for you?',
      'Is your home network secure from hackers?',
      'What are your kids looking at online?',
      'Personalized learning, in as little as 15 minutes',
    ],
    image: getPartnerResource('Discovery_divein.svg'),
    imageMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { left: true, top: true, bottom: true }, mobile: { bottom: true } },
  },
];

const AsurionProtectItCta = styled(ProtectItCta)`
  background-color: ${(props) => props.theme.colors.primaryAccent} !important;
  text-transform: uppercase;
`;

const HeroTitle = styled(Heading1)`
  font-weight: 300;
  margin-bottom: 25px;
  max-width: 700px;
`;

const HeroSubtitle = styled(Subtitle)`
  color: #1b1b1b;
`;

const CalculatorTitle = styled(CalculatorHeader)`
  font-weight: 300;
`;

const ProtectItSubtitleWrapper = styled.div`
  font-weight: 300;
  color: #000;
`;

const ProtectItSubtitle = () => {
  return (
    <ProtectItSubtitleWrapper>
      With Home+ by Asurion you get hassle-free protection for cracked screens, spills, or defects. Expert tech support
      for everything else. All in one place. Seriously.
    </ProtectItSubtitleWrapper>
  );
};

const calculatorComponents = {
  LogoContainer: styled.div`
    box-sizing: border-box;
    padding: 0 5px;
    height: 40px;

    @media ${BREAK_POINTS.tablet} {
      padding: 0 50px;
      height: 50px;
    }
  `,
  Logo: styled.img`
    width: 100%;
    height: 100%;
  `,
};

const CalculatorLogo = () => (
  <calculatorComponents.LogoContainer>
    <calculatorComponents.Logo src={getPartnerResource('calc-logo.svg')} />
  </calculatorComponents.LogoContainer>
);

const HeroImageContainer = styled.div`
  height: 100%;
  width: 100%;
  background-image: ${`url(${getPartnerResource('asurion-hero-mobile.jpg')})`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position-x: center;
  background-color: #e6e6eb;

  @media ${BREAK_POINTS.tablet} {
    background-image: ${`url(${getPartnerResource('asurion-hero-tablet.jpg')})`};
  }

  @media ${BREAK_POINTS.tabletBig} {
    background-image: ${`url(${getPartnerResource('asurion-hero-tablet-big.jpg')})`};
  }

  @media ${BREAK_POINTS.desktop} {
    background-image: ${`url(${getPartnerResource('asurion-hero-desktop.jpg')})`};
  }

  @media ${BREAK_POINTS.desktopBig} {
    background-image: ${`url(${getPartnerResource('asurion-hero-desktop-big.jpg')})`};
  }
`;

const ProtectItHeader = styled(ProtectItTitle)`
  font-weight: 300;
  color: #000;

  @media ${BREAK_POINTS.desktop} {
    font-size: 60px;
  }
`;

const ProtectItContainerWrapper = styled(ProtectItContainer)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-position-y: 5%;

  @media ${BREAK_POINTS.tablet} {
    background-image: ${`url(${getPartnerResource('asurion-protect-it-tablet.jpg')})`};
  }

  @media ${BREAK_POINTS.tabletBig} {
    background-image: ${`url(${getPartnerResource('asurion-protect-it-tablet-big.jpg')})`};
  }

  @media ${BREAK_POINTS.desktop} {
    background-image: ${`url(${getPartnerResource('asurion-protect-it-desktop.jpg')})`};
  }

  @media ${BREAK_POINTS.desktopBig} {
    background-image: ${`url(${getPartnerResource('asurion-protect-it-desktop-big.jpg')})`};
    background-position-y: 36%;
  }
`;

const ProtectItTextContainerWrapper = styled(ProtectItTextContainer)`
  margin-top: 0;
  margin-bottom: 5%;
`;

const ValuePropTitleWrapper = styled(ValuePropTitle)`
  font-weight: 300;
`;

const CalculatorResultsHeadingWrapper = styled(CalculatorResultsHeader)`
  font-weight: 300;
`;

const FinePrintTitleWrapper = styled(FinePrintTitle)`
  font-weight: 300;
`;

const testimonialTitleWrapper = styled(TestimonialTitle)`
  font-weight: 300;
`;

const ValuePropImageContainerWrapper = styled(ValuePropositionImageContainer)`
  background-image: ${(props: { src: any }) => `url(${props.src})`};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  @media ${BREAK_POINTS.desktop} {
    background-size: 100% 60%;
  }
`;

const SignupTitle = styled(EnrollmentTitle)`
  font-weight: 300;
  margin-bottom: 1em;
`;

const HeroContentSpacerWrapper = styled(HeroContentSpacer)`
  flex-basis: 3%;

  @media ${BREAK_POINTS.tablet} {
    flex-basis: 15%;
  }

  @media ${BREAK_POINTS.tabletBig} {
    flex-basis: 25%;
  }
`;

const SignupSubtitleWrapper = styled(Heading4)`
  font-weight: 300;
  margin: 0;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const getMobileSignupImageSizeByStep = (step: number): string => {
  if (step === 1) return '150px';
  if (step === 2) return '120px';
  if (step === 3) return '190px';
  return '120px';
};

const getTabletSignupImageSizeByStep = (step: number): string => {
  if (step === 1) return '320px';
  if (step === 2) return '260px';
  if (step === 3) return '440px';
  return '120px';
};

const SignupImageContainerWrapper = styled(SignupImageContainer)`
  background-image: ${(props) => `url(${props.image})`};
  background-repeat: no-repeat;
  background-position-y: top;
  background-size: ${(props) => getMobileSignupImageSizeByStep(props.step)};

  @media ${BREAK_POINTS.tablet} {
    background-size: ${(props) => getTabletSignupImageSizeByStep(props.step)};
  }

  @media ${(props) => (props.step === 5 ? BREAK_POINTS.tablet : BREAK_POINTS.desktop)} {
    background-image: ${(props) => `url(${props.image})`};
    background-repeat: no-repeat;
    background-size: 100% 400px;
    background-position-x: center;
    background-position-y: 30%;
  }
`;

const ProductValueBackground = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 0;
  margin-bottom: -20px;
  width: 100%;
  @media ${BREAK_POINTS.desktop} {
    width: auto;
  }
`;

const ProductValueTextContainer = styled.div`
  z-index: 0;
  padding: 12px;
  width: fill-available;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  @media ${BREAK_POINTS.desktop} {
    padding: 25px;
    max-width: initial;
  }
`;

const getPriceToDisplay = (signupStep: number, price: number, tax: number) =>
  signupStep <= RESIDENCE_PAGE_NUMBER ? getTotalPurchaseAmount(price, 0) : getTotalPurchaseAmount(price, tax);

const PricingTextContainer = styled.div`
  display: flex;
`;

const PricingTextBold = styled.span`
  font-weight: 600;
  font-size: 16px;

  @media ${BREAK_POINTS.tablet} {
    font-size: 18px;
  }
`;

const PricingContext = styled(PricingTextBold)`
  font-weight: 300;
`;

export type ProductValueProps = {
  step: number;
  price: number;
  tax: number;
  subtitle: string;
};

const SignupPricingComponent: React.SFC<ProductValueProps> = (props) => {
  const shouldShowPrice = props.step < REVIEW_PAGE_NUMBER;
  const pricingText = `$${getPriceToDisplay(props.step, props.price, props.tax)}/month`;
  const pricingContext = props.step <= RESIDENCE_PAGE_NUMBER ? '+ sales tax' : 'including tax';

  return (
    <ProductValueBackground>
      <ProductValueTextContainer>
        <SignupSubtitleWrapper>{props.subtitle}</SignupSubtitleWrapper>
        {shouldShowPrice && (
          <PricingTextContainer>
            <PricingTextBold>{pricingText}</PricingTextBold>
            &nbsp;
            <PricingContext>{pricingContext}</PricingContext>
          </PricingTextContainer>
        )}
      </ProductValueTextContainer>
    </ProductValueBackground>
  );
};

const WhatsCoveredCallToActionTitleWrapper = styled(WhatsCoveredCallToActionTitle)`
  font-weight: 300;
`;

const ConfirmationCallButton = styled.a`
  ${CallToAction}
  background-color: ${(props) => props.theme.colors.primaryAccent};
  color: white;
`;

const HeaderMobileCta = styled(HeaderCallToAction)`
  color: white;
  background: ${(props) => props.theme.colors.primaryAccent};
  font-weight: 600;
  border: none;
  padding: 16px 30px;
`;

const AgreementContainer = styled(BodyText)`
  color: ${(props) => props.theme.colors.primaryAccent};
  text-align: center;
  display: flex;
  justify-content: center;
  margin-bottom: 2em;
  margin-top: 2em;
`;

const AgreementText = styled.div`
  color: #636363;
`;

const agreementContainerAsset = () => (
  <AgreementContainer>
    <AgreementText>
      By clicking next, I agree to the{' '}
      <a
        style={{ color: '#4F6CDE' }}
        rel="noreferrer"
        target="_blank"
        href="https://www.asurion.com/homeplus/terms-of-service"
      >
        terms of service
      </a>{' '}
      and{' '}
      <a style={{ color: '#4F6CDE' }} rel="noreferrer" target="_blank" href="https://www.asurion.com/privacy-policy/">
        privacy policy
      </a>
      .
    </AgreementText>
  </AgreementContainer>
);

const HOME_CALL_TO_ACTION = `JOIN ${PRODUCT_NAME}`;

const assets: DynamicAssets = {
  [DynamicAssetsKeys.contactAgreementContainer]: agreementContainerAsset,
  [DynamicAssetsKeys.chatUrl]: CHAT_URL,
  [DynamicAssetsKeys.expertSessionsPath]: EXPERT_SESSIONS_PATH,
  [DynamicAssetsKeys.expertSessionsCallMeLaterSubtitle]: '',
  [DynamicAssetsKeys.loggedInFooterNavLinks]: footerLinks,
  [DynamicAssetsKeys.footerNavLinks]: footerLinks,
  [DynamicAssetsKeys.loggedInHeaderNavLinks]: loggedInHeaderLinks,
  [DynamicAssetsKeys.headerNavLinks]: headerLinks,
  [DynamicAssetsKeys.protectItImage]: require('./resources/SHS-ASURION/asurion-protect-it-mobile.jpg'),
  [DynamicAssetsKeys.headerMobileCtaComponent]: (props: any) => (
    <HeaderMobileCta {...props}>{HOME_CALL_TO_ACTION}</HeaderMobileCta>
  ),

  [DynamicAssetsKeys.homeCallToAction]: HOME_CALL_TO_ACTION,

  [DynamicAssetsKeys.footerPartnerLogoImage]: null,
  [DynamicAssetsKeys.footerPartnerLogoHref]: null,

  [DynamicAssetsKeys.footerOnlineClaimButtonEnabled]: false,

  [DynamicAssetsKeys.confirmationPageDetailsLogo]: getPartnerResource('confirmation-logo.png'),

  [DynamicAssetsKeys.productName]: PRODUCT_NAME,

  [DynamicAssetsKeys.finePrintIcon1]: getPartnerResource(DynamicAssetsKeys.finePrintIcon1),
  [DynamicAssetsKeys.finePrintIcon2]: getPartnerResource(DynamicAssetsKeys.finePrintIcon2),
  [DynamicAssetsKeys.finePrintIcon3]: getPartnerResource(DynamicAssetsKeys.finePrintIcon3),

  [DynamicAssetsKeys.homeHeroSpacerComponent]: HeroContentSpacerWrapper,
  [DynamicAssetsKeys.homeHeroTitle]: <>Protect your TVs, laptops, tablets and more.</>,
  [DynamicAssetsKeys.homeHeroTitleComponent]: HeroTitle,
  [DynamicAssetsKeys.homeHeroSubtitle]: <>All under one simple plan</>,
  [DynamicAssetsKeys.homeHeroSubtitleComponent]: HeroSubtitle,
  [DynamicAssetsKeys.homeHeroImage]: '',
  [DynamicAssetsKeys.homeHeroImageContainerComponent]: HeroImageContainer,

  [DynamicAssetsKeys.valueCalculatorHeading1]: (
    <>
      The technology in your home isn&rsquo;t cheap<DisclaimerLink>1</DisclaimerLink>...
    </>
  ),
  [DynamicAssetsKeys.valueCalculatorHeading2]: '',
  [DynamicAssetsKeys.valueCalculatorHeadingComponent]: CalculatorTitle,
  [DynamicAssetsKeys.valueCalculatorSubtitle]: 'What could it cost you to replace it?',

  [DynamicAssetsKeys.homeProtectItTitleComponent]: ProtectItHeader,
  [DynamicAssetsKeys.homeProtectItTitle]: <>Cover it with one simple plan</>,
  [DynamicAssetsKeys.homeProtectItSubtitle]: (
    <>
      With Home+ by Asurion you get hassle-free protection for cracked screens, spills, or defects. Expert tech support
      for everything else. All in one place. Seriously.
    </>
  ),
  [DynamicAssetsKeys.homeProtectItSubtitleComponent]: ProtectItSubtitle,
  [DynamicAssetsKeys.homeProtectItSubtitle2]: '',
  [DynamicAssetsKeys.homeProtectItCtaComponent]: (props: any) => (
    <AsurionProtectItCta {...props}>{HOME_CALL_TO_ACTION}</AsurionProtectItCta>
  ),
  [DynamicAssetsKeys.homeProtectItTextContainer]: ProtectItTextContainerWrapper,
  [DynamicAssetsKeys.homeProtectItContainer]: ProtectItContainerWrapper,

  [DynamicAssetsKeys.homeValuePropTitleComponent]: ValuePropTitleWrapper,
  [DynamicAssetsKeys.homeValuePropImageContainer]: ValuePropImageContainerWrapper,
  [DynamicAssetsKeys.homeValuePropContent]: valuePropositionContent,

  [DynamicAssetsKeys.calculatorResultsHeadingComponent]: CalculatorResultsHeadingWrapper,
  [DynamicAssetsKeys.calculatorResultsHeading]: `A smarter plan that gives you more`,
  [DynamicAssetsKeys.calculatorResultsSubheading]: '',
  [DynamicAssetsKeys.calculatorResultsIndividualHeading]: 'Other Extended Warranties',
  [DynamicAssetsKeys.calculatorResultsTableData]: (maxPrice: number) => [
    [<>The price?</>, <>Only $25/month</>, <CompetitorPriceCell maxPrice={maxPrice} />],
    [<>When can I buy the coverage?</>, <>Anytime</>, <>Typically within 30 days</>],
    [<>What if my TV is 3 years old?</>, <>It's covered</>, <>Probably out of luck</>],
    [<>What if I buy something new?</>, <>Still just $25/month</>, <>Another plan, another payment</>],
    [<>24/7 technical support?</>, <>Included</>, <>Up to $200 for 2 years</>],
    [<>Learning with experts?</>, <>Included</>, <>Up to $99/session</>],
    [<>Do I need to keep receipts?</>, <>No</>, <>Typically</>],
  ],
  [DynamicAssetsKeys.calculatorResultsLogoComponent]: CalculatorLogo,
  [DynamicAssetsKeys.calculatorDisclaimer2]: `Based on your answers from above. Other warranties estimate based on
    total upfront costs for available protection plans.`,

  [DynamicAssetsKeys.finePrintTitleComponent]: FinePrintTitleWrapper,
  [DynamicAssetsKeys.finePrintTitle]: (
    <>
      The fine print.
      <br />
      Magnified.
    </>
  ),
  [DynamicAssetsKeys.finePrintSection1Title]: 'Is it really only $25/mo?',
  [DynamicAssetsKeys.finePrintSection1Detail]: (
    <>It&rsquo;s $25 a month, plus sales tax - determined by the state you live in. Cancel at anytime.</>
  ),
  [DynamicAssetsKeys.finePrintSection2Title]: 'Any hidden fees or deductibles?',
  [DynamicAssetsKeys.finePrintSection2Details]: (
    <>
      No hidden fees. Some devices have a $49 or $99 service fee which includes parts, labor and replacement if needed.
    </>
  ),
  [DynamicAssetsKeys.finePrintSection3Title]: 'Is there a claim limit?',
  [DynamicAssetsKeys.finePrintSection3Details]: (
    <>File as many claims as you need. Maximum coverage is $2,000/claim, and $5,000/year</>
  ),
  [DynamicAssetsKeys.finePrintLinkEnabled]: true,
  [DynamicAssetsKeys.finePrintLinkText]: 'See terms of service for complete details',
  [DynamicAssetsKeys.finePrintLinkTo]: 'https://www.asurion.com/homeplus/terms-of-service',
  [DynamicAssetsKeys.finePrintLinkLabel]: 'Terms of Service',

  [DynamicAssetsKeys.testimonialTitleComponent]: testimonialTitleWrapper,
  [DynamicAssetsKeys.testimonialTitle]: 'Powered by Asurion.',
  [DynamicAssetsKeys.testimonialSubtitle]: '',
  [DynamicAssetsKeys.testimonialBody]: (
    <>
      For more than 20 years, Asurion has helped millions of mobile phone customers recover from cracked screens, spills
      and other issues, with a 4.8 average rating.
      <br />
      <br />
      Home+ by Asurion extends our leading mobile protection and expert support services to help you protect, connect
      and enjoy the tech in your home.
    </>
  ),

  [DynamicAssetsKeys.signupSubtitleComponent]: SignupSubtitleWrapper,
  [DynamicAssetsKeys.signupTitleComponent]: SignupTitle,
  [DynamicAssetsKeys.signupImageComponent]: SignupImageContainerWrapper,
  [DynamicAssetsKeys.signupPricingComponent]: SignupPricingComponent,

  [DynamicAssetsKeys.signupValueProp1]: 'Your home tech, covered.',
  [DynamicAssetsKeys.signupValueProp2]: 'Expert support available 24/7.',
  [DynamicAssetsKeys.signupValueProp3]: <>Accidents happen. Now you&rsquo;re ready.</>,

  [DynamicAssetsKeys.signupImage1]: getPartnerResource('expertise_techyoulove.svg'),
  [DynamicAssetsKeys.signupImage2]: getPartnerResource('expert_male.svg'),
  [DynamicAssetsKeys.signupImage3]: getPartnerResource('BecauseThingsHappen.svg'),
  [DynamicAssetsKeys.signupImage4]: getPartnerResource('man_with_computer.svg'),
  [DynamicAssetsKeys.signupImage5]: getPartnerResource('device_crowd_surf.svg'),

  [DynamicAssetsKeys.contactSubmitButtonId]: 'contact-submit-hplus',
  [DynamicAssetsKeys.residenceSubmitButtonId]: 'residence-submit-hplus',
  [DynamicAssetsKeys.billingSubmitButtonId]: 'billing-submit-hplus',
  [DynamicAssetsKeys.reviewSubmitButtonId]: 'enroll-submit-hplus',
  [DynamicAssetsKeys.confirmationCallButtonId]: 'confirmation-call-button-id-hplus',
  [DynamicAssetsKeys.confirmationChatButtonId]: 'confirmation-chat-button-id-hplus',
  [DynamicAssetsKeys.confirmationExpertSessionsButtonId]: 'confirmation-expert-sessions-button-id-hplus',

  [DynamicAssetsKeys.confirmationWelcomeMessage]: `It’s official! You’re covered. You now have protection and unlimited
    support for your smart home.`,
  [DynamicAssetsKeys.confirmationChatHeading]: `Your team of experts is ready for you.`,
  [DynamicAssetsKeys.confirmationChatMessage]: `Get real time help now! Your ${PRODUCT_NAME} team is ready to tackle
    any issue; from setting up new devices to trouble shooting and getting the most from existing tech.`,
  [DynamicAssetsKeys.confirmationCallMessage]: `Talk to a real person—no waiting, no robots.`,
  [DynamicAssetsKeys.confirmationCallHeading]: 'Give us a call:',
  [DynamicAssetsKeys.confirmationCallComponent]: () => (
    <ConfirmationCallButton className={'confirmation-call-button-id-hplus'} href={phoneToUrl(HELP_NUMBER)}>
      {HELP_NUMBER}
    </ConfirmationCallButton>
  ),

  [DynamicAssetsKeys.whatsCoveredCallToActionTitle]: 'Your home technology, one simple plan.',
  [DynamicAssetsKeys.whatsCoveredCallToActionComponent]: WhatsCoveredCallToActionTitleWrapper,
  [DynamicAssetsKeys.whatsCoveredCallToActionInverted]: false,

  [DynamicAssetsKeys.expertChatAppKey]: '21ca6778-841f-4def-83f4-7cdda02390a5',
  [DynamicAssetsKeys.expertChatSdkEnabled]: true,
};

export default assets;



// WEBPACK FOOTER //
// ./packages/smart-home-support/assets/SHS-ASURION.tsx